import React, { useState } from 'react';
import { Formik, Form, Field } from 'formik';
import {
    TextField,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormControl,
    MenuItem,
    Select,
    Checkbox,
    Box,
} from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import FooterTailwind from 'TailwindcssComponents/Footer';
import TailwindLayout from 'TailwindcssComponents/TailwindLayout';
import TopBar from 'TailwindcssComponents/TopBar';

const renderField = (field) => {
    switch (field.type) {
        case 'text':
            return <Field as={TextField} fullWidth name={field.name} type="text" variant="outlined" />;
        case 'number':
            return <Field as={TextField} fullWidth name={field.name} type="number" variant="outlined" inputProps={{ min: 1 }} />;
        case 'phone':
            return <Field as={TextField} fullWidth name={field.name} type="tel" variant="outlined" placeholder="eg.+94712345678" />;
        case 'email':
            return <Field as={TextField} fullWidth name={field.name} type="email" variant="outlined" placeholder="eg. abc@zmail.com" />;
        case 'image':
            return <Box marginTop={1}><img src={field.formImage} width="150" height="200" /></Box>;
        case 'radio':
            return (
                <FormControl component="fieldset">
                    {/* <FormLabel component="legend">{field.name}</FormLabel> */}
                    <RadioGroup row sx={{ marginLeft: 2 }}>
                        {field.options.split(',').map((options) => (
                            <FormControlLabel
                                key={options}
                                control={<Field as={Radio} type="radio" />}
                                label={options}
                                value={options}
                                name={field.name}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            );
        case 'Select':
            return (
                <Field name={field.name}>
                    {({ field: { value, name }, form: { setFieldValue } }) => (
                        <FormControl fullWidth variant="outlined">
                            <Select
                                value={value}
                                onChange={(e) => setFieldValue(name, e.target.value)}
                                displayEmpty
                                sx={{ height: '45px' }}
                            >
                                <MenuItem value="">
                                    <em>Select an option</em>
                                </MenuItem>
                                {field.options.split(',').map((options) => (
                                    <MenuItem key={options} value={options}>
                                        {options}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                </Field>
            );
        case 'Date':
            return <Field as={TextField} fullWidth name={field.name} type="date" variant="outlined" InputLabelProps={{ shrink: true }} />;
        case 'Checkbox':
            return (
                <FormControlLabel
                    control={<Field as={Checkbox} name={field.name} />}
                    label={field.options}
                />
            );
        case 'Password':
            return <Field as={TextField} fullWidth name={field.name} type="password" variant="outlined" />;
        default:
            return null;
    }
};

const EditRegistration = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const bearerToken = localStorage.getItem('token');
    const location = useLocation();
    const { registrationData = {}, formFieldData = [] } = location.state || {};
    const [isSubmitting, setIsSubmitting] = useState(false);

    const defaultFields = [
        { name: 'Name', type: 'text' },
        { name: 'Email', type: 'email' },
        { name: 'Phone', type: 'phone' }
    ];
    // merging default fields with dynamic fields and remove duplicate if it is in default
    const mergedFields = [
        ...defaultFields,
        ...formFieldData.filter(f => !['Name', 'Email', 'Phone'].includes(f.name))
    ];

    const initialValues = mergedFields.reduce((acc, field) => {
        const fieldName = field.name;

        if (['Name', 'Email', 'Phone'].includes(fieldName)) {
            acc[fieldName] = registrationData[fieldName.toLowerCase()] || '';
        } else {
            acc[fieldName] = registrationData.details?.[fieldName] || '';
        }

        return acc;
    }, {});

    const validate = (values) => {
        const errors = {};
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^\+?[1-9]\d{1,14}$/;

        mergedFields.forEach((field) => {
            const isFieldRequired = defaultFields.includes(field) || field.isRequired;

            if (isFieldRequired && field.type !== 'image') {
                if (!values[field.name]) {
                    errors[field.name] = `${field.name} is required`;
                }
            }
            if (field.type === 'email' && values[field.name]) {
                if (!emailRegex.test(values[field.name])) {
                    errors[field.name] = `${field.name} must be a valid email address`;
                }
            }
            if (field.type === 'phone' && values[field.name]) {
                if (!phoneRegex.test(values[field.name])) {
                    errors[field.name] = `${field.name} must be a valid phone number`;
                }
            }
        });

        return errors;
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            setIsSubmitting(true);
            const { Name: name, Email: email, Phone: phone, ...details } = values;

            const payload = {
                name,
                email,
                phone,
                details,
                userId: registrationData.userId,
            };

            const response = await axios.put(
                `${apiUrl}event-service/event-registration/${id}`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${bearerToken}`,
                        'Content-Type': 'application/json',
                        Accept: 'application/json'
                    }
                }
            );

            if (!response.data.success) throw new Error("Failed to update registration");

            toast.success("Data updated successfully", {
                onClose: () => {
                    setIsSubmitting(false);
                    navigate(-1);
                },
            });

        } catch (error) {
            console.error("Error submitting form:", error);
            toast.error(error?.message || "An error occurred while updating the registration.");
            setIsSubmitting(false);
        }
    };

    return (
        <TailwindLayout>
            <TopBar />
            <div className="p-12 bg-gray-100 min-h-screen mb-8">
                <div className="bg-white rounded-lg shadow-lg overflow-hidden p-6">
                    <h2 className="text-xl font-semibold mb-4">Edit Registration Details</h2>
                    <div className='border-t pt-6'>
                        <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
                            {({ values, errors, touched }) => (
                                <Form>
                                    <div>
                                        {mergedFields.map((field) => (
                                            <div key={field.name} className="mb-4">
                                                <label className="block text-lg font-semibold mb-2">
                                                    {field.name}
                                                </label>
                                                {renderField(field)}
                                                {errors[field.name] && touched[field.name] && (
                                                    <p className="mt-1 text-sm text-red-600">
                                                        {errors[field.name]}
                                                    </p>
                                                )}
                                            </div>
                                        ))}

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Update
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>
            </div>
            <FooterTailwind />
            <ToastContainer
                position="bottom-right"
                autoClose={2000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
        </TailwindLayout>
    );
};

export default EditRegistration;